import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';

interface Props {
  tau: number;
}

// Visualizes E = 1 − e^(−years / tau): the experience factor that multiplies an
// expert's profession contribution. It rises fast early then plateaus toward 1;
// τ is the years to reach ~63.2% of the way up. Updates live as tau is edited.
export const ExperienceCurveChart: React.FC<Props> = ({ tau }) => {
  const valid = Number.isFinite(tau) && tau > 0;
  const t = valid ? tau : 5;

  const { years, values } = useMemo(() => {
    const maxYears = Math.max(20, Math.ceil(t * 4));
    const xs: number[] = [];
    const ys: number[] = [];
    for (let y = 0; y <= maxYears; y += 1) {
      xs.push(y);
      ys.push(1 - Math.exp(-y / t));
    }
    return { years: xs, values: ys };
  }, [t]);

  return (
    <Box sx={{ width: '100%' }}>
      <LineChart
        height={280}
        xAxis={[{ data: years, label: 'Years of experience' }]}
        yAxis={[{ min: 0, max: 1, label: 'Experience factor (E)' }]}
        series={[
          {
            data: values,
            label: 'E = 1 − e^(−years / τ)',
            color: '#AB854A',
            area: true,
            showMark: false,
            curve: 'monotoneX',
          },
        ]}
        margin={{ left: 60, right: 20, top: 20, bottom: 50 }}
        slotProps={{ legend: { hidden: true } }}
      >
        {/* τ marks ~63.2% of the climb; the plateau is everything past ~3τ. */}
        <ChartsReferenceLine
          x={t}
          label={`τ = ${t}`}
          labelAlign="start"
          lineStyle={{ stroke: 'var(--muted)', strokeDasharray: '4 4' }}
        />
        <ChartsReferenceLine
          y={0.632}
          lineStyle={{ stroke: 'var(--muted)', strokeDasharray: '4 4' }}
        />
      </LineChart>
      <Typography color="var(--text-2)" sx={{ fontSize: 12, textAlign: 'center' }}>
        At τ years an expert reaches ~63% of the experience factor; the curve then
        plateaus toward 1.0.
      </Typography>
    </Box>
  );
};
